import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'
import {
  Timeline,
  Delete,
  DeleteSweep,
  Visibility,
  ExpandMore,
  Edit,
  Add,
  CheckCircle,
  Cancel,
  Schedule,
  Archive,
  Create
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import axios from 'axios'

const TIPO_CAMBIO_ICONS = {
  creacion: <Create />,
  edicion: <Edit />,
  cambio_estado: <Schedule />,
  aprobacion: <CheckCircle />,
  rechazo: <Cancel />,
  archivo: <Archive />
}

const TIPO_CAMBIO_COLORS = {
  creacion: 'success',
  edicion: 'primary',
  cambio_estado: 'warning',
  aprobacion: 'success',
  rechazo: 'error',
  archivo: 'default'
}

const TIPO_CAMBIO_LABELS = {
  creacion: 'Creación',
  edicion: 'Edición',
  cambio_estado: 'Cambio de Estado',
  aprobacion: 'Aprobación',
  rechazo: 'Rechazo',
  archivo: 'Archivo'
}

const ControlCambios = ({ procedimientoId }) => {
  const { user, isAdmin, isEditor } = useAuth()
  const [cambios, setCambios] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [selectedCambio, setSelectedCambio] = useState(null)
  const [openDetailDialog, setOpenDetailDialog] = useState(false)
  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false)

  useEffect(() => {
    if (procedimientoId && (isAdmin || isEditor)) {
      loadCambios()
    }
  }, [procedimientoId, isAdmin, isEditor])

  const loadCambios = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get(
        `/api/comentarios/cambios/procedimiento/${procedimientoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setCambios(response.data || [])
    } catch (error) {
      console.error('Error cargando cambios:', error)
      setError('Error al cargar historial de cambios')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCambio = async (cambioId) => {
    if (!window.confirm('¿Está seguro de eliminar este registro de cambio?')) return

    setLoading(true)
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/comentarios/cambios/${cambioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Registro de cambio eliminado exitosamente')
      loadCambios()
    } catch (error) {
      setError('Error al eliminar registro: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAllCambios = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/comentarios/cambios/procedimiento/${procedimientoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Historial de cambios eliminado exitosamente')
      setOpenDeleteAllDialog(false)
      loadCambios()
    } catch (error) {
      setError('Error al eliminar historial: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleViewDetail = (cambio) => {
    setSelectedCambio(cambio)
    setOpenDetailDialog(true)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatJsonValue = (value) => {
    if (!value) return 'N/A'

    try {
      const parsed = JSON.parse(value)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return value
    }
  }

  // Solo mostrar para admin y editor
  if (!isAdmin && !isEditor) {
    return null
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Card>
        <CardHeader
          avatar={<Timeline />}
          title="Control de Cambios"
          subheader="Historial de modificaciones del procedimiento"
          action={
            isAdmin && cambios.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteSweep />}
                onClick={() => setOpenDeleteAllDialog(true)}
              >
                Eliminar Historial
              </Button>
            )
          }
        />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {loading && (
            <Typography>Cargando historial de cambios...</Typography>
          )}

          {!loading && cambios.length === 0 && (
            <Alert severity="info">
              No hay registros de cambios para este procedimiento
            </Alert>
          )}

          {!loading && cambios.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Tipo de Cambio</TableCell>
                    <TableCell>Campo</TableCell>
                    <TableCell>Versión</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cambios.map((cambio) => (
                    <TableRow key={cambio.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(cambio.created_at)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {cambio.usuario_nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cambio.usuario_email}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={TIPO_CAMBIO_ICONS[cambio.tipo_cambio]}
                          label={TIPO_CAMBIO_LABELS[cambio.tipo_cambio]}
                          color={TIPO_CAMBIO_COLORS[cambio.tipo_cambio]}
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {cambio.campo_modificado || 'N/A'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {cambio.version_anterior && cambio.version_nueva ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {cambio.version_anterior} → {cambio.version_nueva}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {cambio.version_nueva || 'N/A'}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {cambio.descripcion_cambio || cambio.comentario_cambio || 'Sin descripción'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Ver Detalles">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetail(cambio)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>

                          {isAdmin && (
                            <Tooltip title="Eliminar Registro">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteCambio(cambio.id)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog de detalle del cambio */}
      <Dialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedCambio && TIPO_CAMBIO_ICONS[selectedCambio.tipo_cambio]}
            Detalle del Cambio
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCambio && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Fecha del Cambio
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {formatDate(selectedCambio.created_at)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Usuario
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {selectedCambio.usuario_nombre} ({selectedCambio.usuario_email})
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tipo de Cambio
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      icon={TIPO_CAMBIO_ICONS[selectedCambio.tipo_cambio]}
                      label={TIPO_CAMBIO_LABELS[selectedCambio.tipo_cambio]}
                      color={TIPO_CAMBIO_COLORS[selectedCambio.tipo_cambio]}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Campo Modificado
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {selectedCambio.campo_modificado || 'N/A'}
                  </Typography>
                </Grid>

                {selectedCambio.version_anterior && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Versión Anterior
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {selectedCambio.version_anterior}
                    </Typography>
                  </Grid>
                )}

                {selectedCambio.version_nueva && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Versión Nueva
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {selectedCambio.version_nueva}
                    </Typography>
                  </Grid>
                )}

                {selectedCambio.descripcion_cambio && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Descripción del Cambio
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {selectedCambio.descripcion_cambio}
                    </Typography>
                  </Grid>
                )}

                {selectedCambio.comentario_cambio && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Comentario
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {selectedCambio.comentario_cambio}
                    </Typography>
                  </Grid>
                )}

                {(selectedCambio.valor_anterior || selectedCambio.valor_nuevo) && (
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle2">
                          Ver Valores Modificados
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {selectedCambio.valor_anterior && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="error.main" gutterBottom>
                                Valor Anterior
                              </Typography>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  bgcolor: 'error.50',
                                  maxHeight: 200,
                                  overflow: 'auto'
                                }}
                              >
                                <Typography
                                  component="pre"
                                  variant="body2"
                                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                >
                                  {formatJsonValue(selectedCambio.valor_anterior)}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                          {selectedCambio.valor_nuevo && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="success.main" gutterBottom>
                                Valor Nuevo
                              </Typography>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  bgcolor: 'success.50',
                                  maxHeight: 200,
                                  overflow: 'auto'
                                }}
                              >
                                <Typography
                                  component="pre"
                                  variant="body2"
                                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                >
                                  {formatJsonValue(selectedCambio.valor_nuevo)}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

                {selectedCambio.aprobado_por_nombre && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Aprobado Por
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {selectedCambio.aprobado_por_nombre} el {formatDate(selectedCambio.aprobado_at)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailDialog(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para eliminar todo */}
      <Dialog open={openDeleteAllDialog} onClose={() => setOpenDeleteAllDialog(false)}>
        <DialogTitle>Confirmar Eliminación del Historial</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción eliminará permanentemente todo el historial de cambios de este procedimiento.
          </Alert>
          <Typography>
            ¿Está seguro de que desea eliminar todo el historial de cambios?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteAllDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteAllCambios}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Eliminar Todo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ControlCambios